/**
 * KAN-204 — "close everything on top, then go". One registry of the overlays
 * that are open right now (drawers, builders, pop-ups, a full page with a form)
 * so a navigation that must leave them (Open Plan & billing from anywhere) can
 * dismiss each one through its OWN close path, newest first.
 *
 * An overlay registers while it is open, with the SAME function its scrim / ✕ /
 * Escape call — so a guarded one asks "Unsaved changes — Stay / Leave" exactly as
 * it does for a tap on ✕, and nothing dirty is ever dropped silently:
 *
 *   registerDismissibleOverlay(close)   // overlay: wait until it unregisters
 *   registerLeaveRequest(ask)           // a page that stays mounted: ask() -> Promise<boolean>
 *
 * `dismissAllOverlays()` resolves true when everything is closed (or answered
 * "leave"), false when the user chose Stay on an unsaved-changes question, or an
 * overlay would not close (a save in flight). The unsaved-changes guard reports
 * its two events here (`noteUnsavedConfirm`, `noteUnsavedStay`); outside a
 * dismissal both are inert.
 */
const entries = []; // { close?, ask? } oldest first
const listeners = new Set();
let confirmsOpen = 0;
let stayEpoch = 0;

export const OVERLAY_DISMISS_SETTLE_MS = 1500;
/** A question left on screen this long stops holding the clock (a dismissal can never wedge forever). */
export const OVERLAY_DISMISS_HARD_CAP_MS = 120000;

const wake = () => { Array.from(listeners).forEach((fn) => fn()); };

function register(entry) {
  entries.push(entry);
  wake();
  return () => {
    const i = entries.indexOf(entry);
    if (i >= 0) entries.splice(i, 1);
    wake();
  };
}

/** An overlay that is open: `close` is its scrim / ✕ / Escape path. Returns the unregister function. */
export function registerDismissibleOverlay(close) {
  return typeof close === 'function' ? register({ close }) : () => {};
}

/** A page that stays mounted until the navigation lands: `ask` runs its leave question, resolving true to go on. */
export function registerLeaveRequest(ask) {
  return typeof ask === 'function' ? register({ ask }) : () => {};
}

/** The unsaved-changes guard shows (true) / drops (false) its Stay / Leave question. */
export function noteUnsavedConfirm(open) {
  confirmsOpen = Math.max(0, confirmsOpen + (open ? 1 : -1));
  wake();
}

/** The user chose Stay: any dismissal in flight is cancelled. */
export function noteUnsavedStay() {
  stayEpoch += 1;
  wake();
}

export function openOverlayCount() {
  return entries.length;
}

/** Resolves true when `isDone()`, false on a Stay or when nothing moved for `settleMs` (a question on screen holds the clock). */
let lastWaitStay = false;
function waitUntil(isDone, epoch, settleMs) {
  return new Promise((resolve) => {
    let timer = null;
    let finished = false;
    const startedAt = Date.now();
    let seenCount = entries.length;
    const finish = (value) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      listeners.delete(check); // eslint-disable-line no-use-before-define
      resolve(value);
    };
    function check() {
      if (stayEpoch !== epoch) { lastWaitStay = true; finish(false); return; }
      if (isDone()) { finish(true); return; }
      // An overlay finished closing: stacked slow exits each get a fresh settle window.
      if (entries.length < seenCount && !finished) arm(); // eslint-disable-line no-use-before-define
      seenCount = entries.length;
    }
    function arm() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (finished) return;
        if (confirmsOpen > 0 && Date.now() - startedAt < OVERLAY_DISMISS_HARD_CAP_MS) arm();
        else finish(false);
      }, settleMs);
    }
    listeners.add(check);
    check();
    if (!finished) arm();
  });
}

/**
 * Dismiss every registered overlay, newest first.
 * @returns {Promise<boolean>} true = nothing left open; false = the user stayed / something would not close
 */
export async function dismissAllOverlays(opts) {
  return (await dismissAllOverlaysWithReason(opts)) === 'done';
}

/**
 * Same walk, with WHY it stopped: 'done' | 'stay' (the user chose Stay / said no — their choice)
 * | 'stuck' (an overlay would not close in time — the caller should say so).
 * @returns {Promise<'done'|'stay'|'stuck'>}
 */
export async function dismissAllOverlaysWithReason({ settleMs = OVERLAY_DISMISS_SETTLE_MS } = {}) {
  const epoch = stayEpoch;
  const handled = new Set();
  for (;;) {
    const next = [...entries].reverse().find((e) => !handled.has(e));
    if (!next) return 'done';
    handled.add(next);
    let ok;
    lastWaitStay = false;
    let declined = false;
    if (next.ask) {
      let answered = false;
      let answer = false;
      let asked;
      try { asked = Promise.resolve(next.ask()); } catch { asked = Promise.resolve(false); }
      asked.then((v) => { answered = true; answer = v === true; wake(); }, () => { answered = true; answer = false; wake(); });
      // eslint-disable-next-line no-await-in-loop
      ok = await waitUntil(() => answered, epoch, settleMs);
      if (ok && !answer) { ok = false; declined = true; }
    } else {
      try { next.close(); } catch { /* an overlay's own bug must not strand the navigation */ }
      // eslint-disable-next-line no-await-in-loop
      ok = await waitUntil(() => !entries.includes(next), epoch, settleMs);
    }
    if (!ok) return declined || lastWaitStay ? 'stay' : 'stuck';
  }
}

/** Sign-out: cancel any dismissal in flight (it resolves as a Stay) and drop open-question bookkeeping. */
export function resetOverlayDismissPending() {
  stayEpoch += 1;
  confirmsOpen = 0;
  wake();
}

export function __resetOverlayDismissBusForTests() {
  entries.length = 0;
  listeners.clear();
  confirmsOpen = 0;
  stayEpoch = 0;
  lastWaitStay = false;
}
